import PurchaseConfiguratorVariantAbstract from "mercor/purchase_configurator/purchase_configurator_variant_abstract";
import BaseController from "confinus_ui/base_controller";
import { localeMessage } from "lib/i18n";
import { closeModal } from "lib/overlay/modal";
import { showNotification } from "lib/overlay/notification";
import { markAsSafeHTML } from "lib/util/safe_html";
import ShoppingCartDropdownController from "mercor/shopping_cart_dropdown_controller";
import {
  Mercor__PurchaseConfigurator__ExtraInfoControllerIdentifier,
  Mercor__PurchaseConfigurator__QuantityCalculatorControllerIdentifier,
  Mercor__PurchaseConfigurator__UomPricingDetailsControllerIdentifier,
} from "controllers/identifiers";

export default class PurchaseConfiguratorController extends BaseController {
  public static outlets = [
    Mercor__PurchaseConfigurator__ExtraInfoControllerIdentifier,
    Mercor__PurchaseConfigurator__QuantityCalculatorControllerIdentifier,
    Mercor__PurchaseConfigurator__UomPricingDetailsControllerIdentifier,
  ];

  public static values = {
    chosenVariant: { type: String, default: null },
  };

  declare private chosenVariantValue: string;

  declare private readonly mercorPurchaseConfiguratorExtraInfoOutlets: PurchaseConfiguratorVariantAbstract[];
  declare private readonly mercorPurchaseConfiguratorQuantityCalculatorOutlets: PurchaseConfiguratorVariantAbstract[];
  declare private readonly mercorPurchaseConfiguratorUomPricingDetailsOutlets: PurchaseConfiguratorVariantAbstract[];

  private get variantControllers() {
    return [
      ...this.mercorPurchaseConfiguratorExtraInfoOutlets,
      ...this.mercorPurchaseConfiguratorQuantityCalculatorOutlets,
      ...this.mercorPurchaseConfiguratorUomPricingDetailsOutlets,
    ];
  }

  public static targets = ["form"];
  declare private readonly formTarget: HTMLFormElement;

  public connect() {
    super.connect();
    // Ensure that all fields except for the chosen variant are initialized as disabled.
    // TODO we can remove this hack once Rails has support for not serializing form inputs inside disabled fieldsets
    // https://github.com/rails/rails/pull/36764
    this.variantControllers.forEach((vc) => {
      vc.isCurrent = vc.id === this.chosenVariantValue;
    });
  }

  public handleVariantChanged(evt: Event) {
    const { value: chosenVariantId } = evt.target as HTMLInputElement;
    this.chosenVariantValue = chosenVariantId;
    this.chosenVariant = chosenVariantId;
  }

  public handleCancelButton(evt: Event) {
    evt.preventDefault();
    // trigger event which parent should listen for
    closeModal();
  }

  // Add-to-cart submit. The form opts out of Turbo Drive (turbo: false) and is
  // submitted here via fetch against the JSON endpoint, which responds with the
  // toast HTML fragment on success (200) or `{message}` JSON on error (422).
  // Replaces the old Rails-UJS ajax:success / ajax:error callbacks.
  public submitForm(evt: SubmitEvent) {
    evt.preventDefault();

    if (!this.isFormValid()) return;

    const form = this.formTarget;
    const body = new FormData(form);
    const method = (
      (body.get("_method") as string | null) ?? form.method
    ).toUpperCase();
    body.delete("_method");

    void this.submitToServer(form.action, method, body);
  }

  private async submitToServer(
    url: string,
    method: string,
    body: FormData,
  ): Promise<void> {
    let response: Response;
    try {
      response = await fetch(url, {
        method,
        body,
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });
    } catch (e) {
      showNotification({
        variant: "error",
        body: localeMessage("generic_server_error"),
        timeout: 5000,
      });
      return;
    }

    if (response.ok) {
      const html = await response.text();
      closeModal();
      this.emitEvent(window, ShoppingCartDropdownController, "update-total", {
        basePath: response.url,
      });
      showNotification({
        variant: "success",
        body: markAsSafeHTML(html),
      });
    } else {
      let message: string | undefined;
      try {
        message = (await response.json()).message;
      } catch (e) {
        // fall through to generic message
      }
      console.error("Failed to submit the purcon form", message);
      showNotification({
        variant: "error",
        body: message ?? localeMessage("generic_server_error"),
        timeout: 5000,
      });
    }
  }

  // Runs the Decor form's own field validation synchronously and returns
  // whether the form is valid. Reuses the form controller's validate event so
  // we don't duplicate field-level validation logic here.
  private isFormValid(): boolean {
    let valid = true;
    this.formTarget.dispatchEvent(
      new CustomEvent("decor--suite--forms--form:validate", {
        bubbles: false,
        detail: {
          onValidated: (isValid: boolean) => {
            valid = isValid;
          },
        },
      }),
    );
    return valid;
  }

  private set chosenVariant(chosenVariantId: string) {
    this.variantControllers.forEach((vc) => {
      vc.isCurrent = vc.id === chosenVariantId;
    });
  }
}
